import { useEffect } from 'react';
import { 
  Dialog,
  DialogContent,
  Typography,
  Box,
  Button
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import FavoriteIcon from '@mui/icons-material/Favorite';
import CelebrationIcon from '@mui/icons-material/Celebration';

interface MatchCelebrationDialogProps {
  open: boolean;
  onClose: () => void;
  onContinue: () => void;
  otherPersonName?: string;
}

export default function MatchCelebrationDialog({ 
  open, 
  onClose, 
  onContinue,
  otherPersonName
}: MatchCelebrationDialogProps) {
  const { t } = useTranslation();

  // Removed auto-continue functionality to let user control the flow

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'rgb(56,25,30)',
          borderRadius: 3,
          color: 'white',
          overflow: 'hidden'
        }
      }}
    >
      <DialogContent sx={{ 
        px: 4, 
        py: 5,
        textAlign: 'center',
        position: 'relative'
      }}>
        {/* Animated heart icons */}
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          pointerEvents: 'none',
          overflow: 'hidden',
          opacity: 0.3
        }}>
          <FavoriteIcon sx={{
            position: 'absolute',
            top: '10%',
            left: '10%',
            fontSize: '2rem',
            color: 'pink',
            animation: 'float 3s ease-in-out infinite',
            '@keyframes float': {
              '0%, 100%': { transform: 'translateY(0px)' },
              '50%': { transform: 'translateY(-20px)' }
            }
          }} />
          <FavoriteIcon sx={{
            position: 'absolute',
            top: '15%',
            right: '15%',
            fontSize: '1.5rem',
            color: 'pink',
            animation: 'float 3s ease-in-out infinite 0.5s',
          }} />
          <FavoriteIcon sx={{
            position: 'absolute',
            bottom: '20%',
            left: '20%',
            fontSize: '1.2rem',
            color: 'pink',
            animation: 'float 3s ease-in-out infinite 1s',
          }} />
          <FavoriteIcon sx={{
            position: 'absolute',
            bottom: '15%',
            right: '10%',
            fontSize: '1.8rem',
            color: 'pink',
            animation: 'float 3s ease-in-out infinite 1.5s',
          }} />
        </Box>

        {/* Celebration icon */}
        <Box sx={{ mb: 3 }}>
          <CelebrationIcon sx={{ 
            fontSize: '4rem', 
            color: '#FFD700',
            animation: 'bounce 1s ease-in-out',
            '@keyframes bounce': {
              '0%, 100%': { transform: 'scale(1)' },
              '50%': { transform: 'scale(1.2)' }
            }
          }} />
        </Box>

        {/* Match message */}
        <Typography variant="h4" sx={{ 
          color: 'white',
          fontWeight: 'bold',
          mb: 2,
          animation: 'fadeIn 0.5s ease-in',
          '@keyframes fadeIn': {
            '0%': { opacity: 0, transform: 'translateY(10px)' },
            '100%': { opacity: 1, transform: 'translateY(0)' }
          }
        }}>
          {otherPersonName 
            ? t('match_celebration.title_with_name', '{{name}} vill träffa dig!', { name: otherPersonName })
            : t('match_celebration.title', 'Det är en match!')}
        </Typography>

        <Typography variant="body1" sx={{ 
          color: 'rgba(255,255,255,0.9)',
          mb: 3,
          animation: 'fadeIn 0.5s ease-in 0.2s backwards',
          fontSize: '1.1rem'
        }}>
          {t('match_celebration.subtitle', 'Ni vill båda träffas!')}
        </Typography>

        <Typography variant="body1" sx={{ 
          color: 'rgba(255,255,255,0.8)',
          mb: 4,
          animation: 'fadeIn 0.5s ease-in 0.4s backwards',
          fontSize: '1.1rem'
        }}>
          {t('match_celebration.next_step', 'Nu ska vi hjälpa er att hitta en tid som passar båda.')}
        </Typography>

        {/* Continue button */}
        <Button 
          onClick={onContinue}
          variant="contained"
          size="large"
          sx={{ 
            bgcolor: '#8E603C',
            color: 'white',
            fontWeight: 'bold',
            minWidth: '200px',
            animation: 'fadeIn 0.5s ease-in 0.6s backwards',
            '&:hover': { 
              bgcolor: '#76451F',
            }
          }}
        >
          {t('match_celebration.continue', 'Fortsätt till tidsbokning')}
        </Button>
      </DialogContent>
    </Dialog>
  );
}